import React, { useMemo, useContext } from "react";
import { Select } from "antd";
import { GlobalContext } from "contexts/GlobalContext";
import {
  FIELD_NAMES,
  GREEK_COLUMNS_WITH_LABELS,
  PRICER_TRADER_GREEKS_STORAGE_KEY,
  getDefaultTraderGreekFields,
} from "constants/pricerTable";
import "./GreeksDropDown.scss";
import {
  getDataFromLocalStorage,
  isTrader,
  setDataToLocalStorage,
} from "helpers/generalHelper";

const { Option } = Select;

const GreekDropDown = (props) => {
  const initialData = getDataFromLocalStorage("greeksDropDown");
  const traderUser = isTrader();
  const slotIndex =
    props.traderGreekSlotIndex ??
    props.column?.getColDef?.()?.headerComponentParams?.traderGreekSlotIndex;
  const isTraderSlot = traderUser && slotIndex != null;

  const dropdownData = useMemo(
    () => ({
      [FIELD_NAMES.BID]: GREEK_COLUMNS_WITH_LABELS.bid,
      [FIELD_NAMES.ASK]: GREEK_COLUMNS_WITH_LABELS.ask,
    }),
    []
  );
  const {
    globalState: { dark_Theme },
  } = useContext(GlobalContext);

  const colDef = props.column?.getColDef?.();
  const colField = colDef?.field;
  const colIdFromDef = colDef?.colId;

  const traderBidOptions = useMemo(
    () => GREEK_COLUMNS_WITH_LABELS.bid,
    []
  );

  const onSelectionChanged = (value) => {
    if (isTraderSlot) {
      const colId = props.column.getColId();
      const pricerCols = props.api.getColumnDefs().map((def) =>
        def.colId === colId ? { ...def, field: value } : def
      );
      const saved =
        getDataFromLocalStorage(PRICER_TRADER_GREEKS_STORAGE_KEY) || {};
      setDataToLocalStorage(PRICER_TRADER_GREEKS_STORAGE_KEY, {
        ...saved,
        [String(slotIndex)]: value,
      });
      props.api.setColumnDefs(pricerCols);
      return;
    }

    const prev = getDataFromLocalStorage("greeksDropDown") || {};
    let selectBid = prev.bid || "bid_delta";
    let selectAsk = prev.ask || "ask_delta";
    const colId = props.column.getColId();
    const pricerCols = props.api.getColumnDefs().map((def) => {
      if (def.colId !== colId) return def;
      if (def.colId.toString().includes("bid")) {
        selectBid = value;
      }
      if (def.colId.toString().includes("ask")) {
        selectAsk = value;
      }
      return { ...def, colId: value, field: value };
    });

    setDataToLocalStorage("greeksDropDown", {
      bid: selectBid,
      ask: selectAsk,
    });

    props.api.setColumnDefs(pricerCols);
  };

  const showHeaderDropDown = useMemo(() => {
    if (isTraderSlot) {
      return traderBidOptions?.map((item) => (
        <Option key={item.key} value={item.key}>
          {item.label.replace(/^Bid\s+/i, "")}
        </Option>
      ));
    }
    const AskOrBidOrCompanydropdownData = colIdFromDef?.includes("ask")
      ? dropdownData[FIELD_NAMES.ASK]
      : dropdownData[FIELD_NAMES.BID];

    return AskOrBidOrCompanydropdownData?.map((item) => {
      return (
        <Option key={item.key} value={item.key}>
          {item.label}
        </Option>
      );
    });
  }, [isTraderSlot, colIdFromDef, dropdownData, traderBidOptions]);

  const returnValue = useMemo(() => {
    if (isTraderSlot) {
      if (colField) return colField;
      const saved =
        getDataFromLocalStorage(PRICER_TRADER_GREEKS_STORAGE_KEY) || {};
      const defaults = getDefaultTraderGreekFields();
      return saved[String(slotIndex)] || defaults[slotIndex];
    }
    const containsAsk = colIdFromDef
      ?.toString()
      .includes(FIELD_NAMES.ASK);

    if (containsAsk) {
      return initialData?.ask || dropdownData[FIELD_NAMES.ASK][0]?.key;
    }
    return initialData?.bid || dropdownData[FIELD_NAMES.BID][0]?.key;
  }, [
    isTraderSlot,
    slotIndex,
    colField,
    colIdFromDef,
    initialData?.ask,
    initialData?.bid,
    dropdownData,
  ]);

  return (
    <Select
      className={` greek-drop-down__container ${
        dark_Theme
          ? "greek-drop-down__container-dark"
          : "greek-drop-down__container-light"
      }`}
      onChange={(val) => onSelectionChanged(val)}
      value={returnValue}
      popupClassName={dark_Theme && "dropdown-dark"}
      style={{
        color: dark_Theme ? "var(--color-white-1)" : "var(--color-black-1)",
      }}
    >
      {showHeaderDropDown}
    </Select>
  );
};

GreekDropDown.displayName = "GreekDropDown";

export default GreekDropDown;
